/**
 * The mail client's answers and its search (piece 32): what the folder tree,
 * the message list, the reading pane, the quarantine review and Settings ›
 * Email › Mailboxes read, typed once so the server's answer and the screen's
 * fixture are the same shape.
 */
import type { AuthResultValue, DeliveryStatusValue, FolderKindValue, MailboxKindValue, MessagePriorityValue, QuarantineReasonValue, SignatureStatusValue, TransportSecurityValue } from './model';
import type { MailUnsubscribeView } from './unsubscribe';
import type { MailReceiptView } from './readReceipt';

/** The mail client's routes and the API paths the browser asks for, relative to the API root (piece 1). */
export const MAIL_API_PATH = '/mail';
export const MAIL_MAILBOXES_PATH = `${MAIL_API_PATH}/mailboxes`;
export const MAIL_MESSAGES_PATH = `${MAIL_API_PATH}/messages`;
export const MAIL_QUARANTINE_PATH = `${MAIL_API_PATH}/quarantine`;
export const MAIL_TAG_TARGETS_PATH = `${MAIL_API_PATH}/tag-targets`;
export const MAIL_SETTINGS_MAILBOXES_PATH = `${MAIL_API_PATH}/settings/mailboxes`;
/** The bulk actions, each over a list of message ids; literal segments the server declares before `messages/:id`. */
export const MAIL_BULK_ACTIONS = ['flags', 'move', 'delete', 'restore', 'report', 'release', 'confirm-rejection'] as const;
export type MailBulkAction = (typeof MAIL_BULK_ACTIONS)[number];
export const mailBulkPath = (action: MailBulkAction): string => `${MAIL_MESSAGES_PATH}/${action}`;
export const mailMessagePath = (id: string): string => `${MAIL_MESSAGES_PATH}/${id}`;
export const mailHeadersPath = (id: string): string => `${mailMessagePath(id)}/headers`;
export const mailTagPath = (id: string): string => `${mailMessagePath(id)}/tag`;
/** 🔒 Print on paper (decision 1539): the message as a PDF the API renders, opened in a tab of its own. */
export const mailPrintPath = (id: string): string => `${mailMessagePath(id)}/pdf`;
export const mailSyncPath = (mailboxId: string): string => `${MAIL_MAILBOXES_PATH}/${mailboxId}/sync`;
export const mailAttachmentPath = (id: string): string => `${MAIL_API_PATH}/attachments/${id}`;
/** Settings › Email › Mailboxes (piece 15; decision 0790). */
export const MAIL_SETTINGS_ROUTE = '/settings/email/mailboxes';

/** One folder in the tree, with what the tree prints beside it. */
export interface MailFolderNode {
  id: string;
  mailboxId: string;
  parentFolderId: string | null;
  name: string;
  kind: FolderKindValue;
  isSystem: boolean;
  unread: number;
  total: number;
}

/** One mailbox in the tree: the viewer's own first, then each shared one they hold a grant on (decision 0790). */
export interface MailboxNode {
  id: string;
  name: string;
  address: string;
  kind: MailboxKindValue;
  /** The viewer's own Personal mailbox. */
  isOwn: boolean;
  /** 🔨 A departed person's mailbox reads and refuses every write. */
  departed: boolean;
  /** Use against `quotaMegabytes?`, in whole percent; null where the mailbox has no quota (decision 0795). */
  quotaPercent: number | null;
  /** No pass has read the host yet: the screen asks for one when it opens. */
  neverSynced: boolean;
  /** How many messages its Quarantine folder holds; a viewer without `mail.quarantine.review` sees this and never the folder (decision 0961). */
  held: number;
  folders: MailFolderNode[];
}

export interface MailTreeAnswer {
  mailboxes: MailboxNode[];
  /** Department mailboxes of departments the viewer heads that they do not read, each opening its record page (decision 0842). */
  manages?: { id: string; name: string; address: string }[];
  /** The viewer may review quarantined mail (`mail.quarantine.review`). */
  canReview: boolean;
}

/** One row of the message list: sender, subject, first line, date, the attachment and flag marks, unread in bold. */
export interface MessageListRow {
  id: string;
  mailboxId: string;
  folderId: string;
  folderKind: FolderKindValue;
  /** The sender on received mail; the first recipient on mail this mailbox sent. */
  correspondent: string;
  fromAddress: string;
  subject: string;
  firstLine: string;
  /** Received on received mail, sent on sent mail, as an instant. */
  date: string | null;
  isRead: boolean;
  isFlagged: boolean;
  hasAttachments: boolean;
  priority: MessagePriorityValue;
  quarantineReason: QuarantineReasonValue | null;
  /** The screening noted it: the reading pane carries a warning band. */
  warned: boolean;
  /** 🔒 Conversation View (decision 1228): the thread's message count in the mailbox; absent in the one-row-per-message list. */
  threadCount?: number;
  /** 🔒 Conversation View: the thread's senders in the order they first wrote, three named and the rest counted. */
  participants?: string;
  /** 🔒 T346 (decision 1515): in the accounting mailbox, the message is tagged to a Bill or a Payment, read off the tag and stored nowhere. */
  dealtWith?: boolean;
}

export interface MessageListAnswer {
  rows: MessageListRow[];
  /** Every match, of which `rows` holds the newest `MAIL_LIST_LIMIT`. */
  total: number;
  /** 🔒 Whether the requester's own `mail.conversationView` is on, so the list is one row per thread (decision 1228). */
  conversationView?: boolean;
  /** 🔒 In Conversation View, how many of the matches the thread rows stand for; `total` still counts messages. */
  messageCount?: number;
}

/** 🔨 The list draws the newest this many matches, and says how many more the search would narrow. */
export const MAIL_LIST_LIMIT = 500;

export interface MailAddressView {
  address: string;
  displayName: string | null;
}

export interface MessageAttachmentView {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: 'Pending' | 'Clean' | 'Blocked';
}

export interface MessageThreadRow {
  id: string;
  correspondent: string;
  date: string | null;
  firstLine: string;
  isCurrent: boolean;
}

export interface MessageDetail {
  id: string;
  mailbox: { id: string; name: string; departed: boolean };
  folder: { id: string; name: string; kind: FolderKindValue };
  from: MailAddressView;
  replyTo: string | null;
  /** 🔨 The header says so plainly when Reply-To is not From. */
  replyToDiffers: boolean;
  to: string[];
  cc: string[];
  subject: string;
  date: string | null;
  priority: MessagePriorityValue;
  isRead: boolean;
  isFlagged: boolean;
  direction: 'Inbound' | 'Outbound';
  security: { transportSecurity: TransportSecurityValue; spfResult: AuthResultValue; dkimResult: AuthResultValue; dmarcResult: AuthResultValue; signatureStatus: SignatureStatusValue | null; /** Decision 1201: the signer's name, stored at first sight beside a Valid verdict. */ signatureSignerName: string | null };
  /** The screening's own reasons, in plain words, where it noted or held the message; empty otherwise. */
  warnings: string[];
  quarantine: { reason: QuarantineReasonValue } | null;
  /**
   * The body as it arrived, which the screen runs through `sanitizeMailHtml` before drawing it, and each inline
   * image's content id with the attachment it is drawn from. 🔨 The server never renders a body.
   */
  body: { html: string; inline: Record<string, string> };
  attachments: MessageAttachmentView[];
  inlineImages: number;
  thread: MessageThreadRow[];
  tag: { recordType: string; recordId: string; label: string; inherited: boolean } | null;
  /**
   * 🔒 Decision 1622: the record a rule with Suggest Tag to Record proposed at first sight, which Tag to Record offers
   * first and already chosen. Null where none was stored or the viewer cannot open it; absent on an older answer.
   */
  suggestion?: MailTagTarget | null;
  deliveryStatus: DeliveryStatusValue | null;
  /** 🔒 Who actually sent it, whatever address shows (decisions 0081, 0791); null on received mail. */
  sentBy: string | null;
  /** The release time of a Held message in Outbox, as an instant; null everywhere else. */
  scheduledSendAt?: string | null;
  /** A tracked send's opens (decision 0162): the count and the last, and null on a send that carries no link. */
  opens?: { count: number; lastOpenedAt: string | null } | null;
  bounceOf: { id: string; subject: string } | null;
  /** The viewer may move, flag, delete and report it: false in a departed mailbox. */
  canOrganise: boolean;
  canReview: boolean;
  /**
   * 🔒 What the sender menu offers (decision 1477): the Person the From address resolves to through its live
   * WorkEmail contact method, never a name, and whether the viewer may read people and block a sender.
   * Absent on an answer from before the menu, where the menu offers neither person row nor Block sender.
   */
  sender?: MessageSender;
  /** 🔒 T346 (decision 1515): the message stands in the mailbox `accounting.inboxAddress` names, so the pane reads it and draws the Looks like block. */
  accountingMailbox?: boolean;
  /** 🔒 T346: the dealt-with mark beside the read mark, read off the message's tag. */
  dealtWith?: boolean;
  /** 🔒 T346b (decision 1516): Send to accounting stands beside Tag to record on this message. */
  sendToAccounting?: boolean;
  /** 🔒 T346b: where the message was sent to accounting and when, read off the forward in its thread; null where it never was. */
  sentToAccounting?: { address: string; at: string; waiting: boolean } | null;
  /**
   * 🔒 T354 (decision 2640): where Unsubscribe goes, read from the stored List-Unsubscribe header when the pane opens.
   * Null on mail this mailbox sent, on a held message, on one carrying a warning band, and where the header names no target.
   */
  unsubscribe?: MailUnsubscribeView | null;
  /**
   * 🔒 T2371 (RFC 3798): where the sender asked a read receipt to go, and whether this mailbox already sent one. Null
   * where the message asks for none or Send receipt is not offered; the system never answers one by itself.
   */
  receipt?: MailReceiptView | null;
}

/** 🔒 The sender menu's answer (decision 1477), read with the message so the menu opens on rows it already knows. */
export interface MessageSender {
  /** The Person the From address resolves to; null where none matches or the viewer cannot read people. */
  person: { id: string; name: string } | null;
  /** The viewer reads the Person module: Open the person stands in the menu where the address resolves. */
  canReadPeople: boolean;
  /** The viewer writes the Person module, which the Person create route asks: Create a person stands where nobody matches. */
  canCreatePeople: boolean;
  /** The viewer holds `admin.operations`, the screening routes' gate: Block sender stands in the menu. */
  canBlock: boolean;
  /** 🔒 T354 (decision 2640): the resolved Person's own `avatarColor`, which the From line's avatar draws in; absent or null for anyone else. */
  avatarColor?: string | null;
}

/** One row of the quarantine review: the specific reason and the failed check (piece 32). */
export interface QuarantineRow {
  id: string;
  mailboxId: string;
  mailboxName: string;
  fromAddress: string;
  correspondent: string;
  subject: string;
  date: string | null;
  reason: QuarantineReasonValue;
  /** The check that failed, in plain words: the screening's strongest signal; null where a person reported it or the host filed it as junk. */
  failedCheck: string | null;
  score: number | null;
}

export interface QuarantineAnswer {
  rows: QuarantineRow[];
}

/** One row of Settings › Email › Mailboxes: address, name, kind, owner and number of open readers (decision 0790). */
export interface MailboxSettingsRow {
  id: string;
  name: string;
  address: string;
  kind: MailboxKindValue;
  owner: string;
  readers: number;
  /** Open grants of any capability, so a SendAs delegation counts toward the Delegated view (decisions 0794, 0841). */
  grants?: number;
  isDistributionList: boolean;
  departed: boolean;
  quotaMegabytes: number | null;
  quotaPercent: number | null;
  /** A distribution list's members, its department's open positions and its Read grants, counted live; null on every other row (decisions 0841, 1237). */
  members?: number | null;
  /** 🔒 What the viewer holds on this mailbox: its owner, its department's head, a Read or a SendAs grant (decision 1237). */
  yourAccess?: MailboxAccessRole[];
  /** 🔒 Whether this is the mailbox `accounting.inboxAddress` names, the Documents Inbox's master (decision 12640). */
  isDocumentsInbox?: boolean;
}

/** The viewer's own standing on a mailbox, as the Mail Settings window's Shared Mailboxes page prints it (decision 1237). */
export const MAILBOX_ACCESS_ROLES = ['Owner', 'Head', 'Read', 'SendAs'] as const;
export type MailboxAccessRole = (typeof MAILBOX_ACCESS_ROLES)[number];

/** The six record kinds Tag to Record offers first, the ones Print names (decision 0797). */
export const MAIL_TAG_KINDS = ['WorkOrder', 'Deal', 'CompanyRecord', 'EmploymentRecord', 'PersonRecord', 'Invoice'] as const;
export type MailTagKind = (typeof MAIL_TAG_KINDS)[number];

export interface MailTagTarget {
  recordType: MailTagKind;
  recordId: string;
  label: string;
}

/** What the list's search asks for; every field is optional and they narrow together. */
export interface MailSearch {
  folderId?: string;
  mailboxId?: string;
  /** Sender, recipient, subject or body, as the search box reads it. */
  q?: string;
  from?: string;
  to?: string;
  subject?: string;
  body?: string;
  /** Instants, converted from the viewer's own calendar days by the screen. */
  since?: string;
  until?: string;
  hasAttachment?: boolean;
  unread?: boolean;
  flagged?: boolean;
}

const TEXT_KEYS = ['folderId', 'mailboxId', 'q', 'from', 'to', 'subject', 'body', 'since', 'until'] as const;
const FLAG_KEYS = ['hasAttachment', 'unread', 'flagged'] as const;

/** The search as a query string, empty fields left out. */
export function mailSearchQuery(search: MailSearch): string {
  const params = new URLSearchParams();
  for (const key of TEXT_KEYS) {
    const value = search[key]?.trim();
    if (value) params.set(key, value);
  }
  for (const key of FLAG_KEYS) if (search[key]) params.set(key, '1');
  return params.toString();
}

/** The search a query string asks for; an unreadable instant is left out rather than guessed. */
export function parseMailSearch(query: Readonly<Record<string, unknown>>): MailSearch {
  const out: MailSearch = {};
  for (const key of TEXT_KEYS) {
    const value = query[key];
    if (typeof value === 'string' && value.trim()) out[key] = value.trim().slice(0, 200);
  }
  for (const key of ['since', 'until'] as const) if (out[key] && Number.isNaN(Date.parse(out[key]!))) delete out[key];
  for (const key of FLAG_KEYS) if (query[key] === '1' || query[key] === 'true') out[key] = true;
  return out;
}

/** The messages sent about one record, which its page lists (decision 0162: the document's email log is a query). */
export const MAIL_RECORD_MESSAGES_PATH = `${MAIL_API_PATH}/record-messages`;

/** How many messages a record's Email panel lists, sends and received mail together (T3317); the answer carries one more when older mail is left out, and the panel says so then (T3339). */
export const MAIL_RECORD_MESSAGES_LIMIT = 50;

/** One message sent about a record, or received and tagged to it (T2371): to whom, from which address, by whom, when, how it stands, and its opens. */
export interface RecordMessageRow {
  id: string;
  /** T2371: a received message tagged to the record lists beside the sends; an answer from before reads every row as a send. */
  direction?: 'Inbound' | 'Outbound';
  /** When a received message arrived; null on a send. */
  receivedAt?: string | null;
  subject: string;
  to: string[];
  fromAddress: string;
  sentBy: string | null;
  sentAt: string | null;
  deliveryStatus: DeliveryStatusValue | null;
  /** 🔒 One row per open (decision 0162); the screen says Link opened and never read. */
  opens: number;
  lastOpenedAt: string | null;
}
